/**
 * CloverDiagnostics — super-admin panel that answers, in one click,
 * "is my Clover setup healthy right now?" It lives on the Admin Dashboard
 * and probes /api/admin/clover/diagnostics which itself does a live GET
 * against Clover to see whether the configured App ID gets force-redirected
 * to sandbox. Prevents the "why is the customer still on sandbox?" debug
 * loop we hit iterations 42–45.
 */
import { useState } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  ShieldCheck, ShieldAlert, RefreshCw, ExternalLink, Copy, Check,
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CloverDiagnostics({ isDark }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  const run = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/clover/diagnostics`);
      setData(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to run Clover diagnostics");
    } finally {
      setLoading(false);
    }
  };

  const copyUrl = async () => {
    if (!data?.sample_auth_url) return;
    await navigator.clipboard.writeText(data.sample_auth_url);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const cardBg = isDark ? "bg-slate-800 border-slate-700" : "bg-white border-slate-200";
  const sub = isDark ? "text-slate-400" : "text-slate-500";
  const heading = isDark ? "text-white" : "text-slate-800";

  const healthy = data?.healthy;
  const HeadingIcon = healthy ? ShieldCheck : ShieldAlert;
  const headingColor = healthy
    ? (isDark ? "text-emerald-400" : "text-emerald-600")
    : (isDark ? "text-amber-400" : "text-amber-600");

  return (
    <Card className={cardBg} data-testid="clover-diagnostics-card">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <CardTitle className={`flex items-center gap-2 ${heading}`}>
            <HeadingIcon className={`w-5 h-5 ${data ? headingColor : sub}`} />
            Clover Integration Diagnostics
            {data && (
              <Badge className={healthy
                ? "bg-emerald-100 text-emerald-700 border border-emerald-300 hover:bg-emerald-100"
                : "bg-amber-100 text-amber-800 border border-amber-300 hover:bg-amber-100"}>
                {healthy ? "Healthy" : `${data.warnings.length} warning${data.warnings.length === 1 ? "" : "s"}`}
              </Badge>
            )}
          </CardTitle>
          <Button
            onClick={run}
            disabled={loading}
            size="sm"
            variant="outline"
            data-testid="clover-diagnostics-run-btn"
          >
            <RefreshCw className={`w-4 h-4 mr-1.5 ${loading ? "animate-spin" : ""}`} />
            {data ? "Re-run" : "Run diagnostics"}
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {!data && !loading && (
          <p className={`text-sm ${sub}`}>
            Run this before/after any Clover redeploy. It probes Clover directly
            to detect sandbox-only App IDs and stale env vars — the two things
            that caused the &quot;customer can&apos;t log in&quot; bug.
          </p>
        )}

        {loading && (
          <div className="flex items-center gap-2 text-sm">
            <div className="w-4 h-4 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin" />
            <span className={sub}>Contacting Clover…</span>
          </div>
        )}

        {data && (
          <div className="space-y-4">
            {/* Warnings first */}
            {data.warnings.length > 0 && (
              <div className={`p-3 rounded-lg border-l-4 border-amber-500 ${isDark ? "bg-amber-950/30" : "bg-amber-50"}`}>
                <p className={`text-sm font-semibold ${isDark ? "text-amber-200" : "text-amber-900"} mb-1`}>
                  Action needed:
                </p>
                <ul className={`list-disc pl-5 text-sm space-y-1 ${isDark ? "text-amber-200" : "text-amber-800"}`}>
                  {data.warnings.map((w, i) => (
                    <li key={i}>{w}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Facts grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Fact label="App ID"                      value={data.app_id_masked || "(missing)"} isDark={isDark} mono />
              <Fact label="App Secret"                  value={data.app_secret_configured ? "configured" : "missing"} isDark={isDark} ok={data.app_secret_configured} />
              <Fact label="Webhook Auth Code"           value={data.webhook_auth_code_configured ? "configured" : "not set — set CLOVER_WEBHOOK_AUTH_CODE"} isDark={isDark} ok={data.webhook_auth_code_configured} />
              <Fact label="Webhook Signing Secret"      value={data.webhook_secret_configured ? "configured" : "not set (optional)"} isDark={isDark} ok={data.webhook_secret_configured} />
              <Fact label="Resolved Environment"        value={data.resolved_environment} isDark={isDark} />
              <Fact label="Production Approved (flag)"  value={data.production_approved ? "YES" : "NO — merchants see 'coming soon' gate"} isDark={isDark} ok={data.production_approved} />
              <Fact label="Live probe host"             value={data.live_probe?.final_url_host || "n/a"} isDark={isDark} mono />
              <Fact label="Sandbox app detected?"       value={data.live_probe?.sandbox_app_detected ? "YES (blocks real merchants)" : "no"} isDark={isDark} ok={!data.live_probe?.sandbox_app_detected} />
              <Fact label="Clover hard-redirect seen?"  value={data.live_probe?.hard_redirect_seen ? "YES" : "no"} isDark={isDark} ok={!data.live_probe?.hard_redirect_seen} />
            </div>

            {/* Webhook callback URL — copyable, this is what merchants paste into Clover Developer Dashboard */}
            {data.webhook_callback_url && (
              <div className={`p-3 rounded-lg ${isDark ? "bg-slate-900/50 border border-slate-700" : "bg-slate-50 border border-slate-200"}`}>
                <div className="flex items-center justify-between mb-1">
                  <span className={`text-xs font-semibold uppercase tracking-wide ${sub}`}>Webhook callback URL (paste into Clover Developer Dashboard → Webhooks)</span>
                  <button
                    onClick={async () => {
                      await navigator.clipboard.writeText(data.webhook_callback_url);
                      toast.success("Webhook URL copied");
                    }}
                    className={`text-xs inline-flex items-center gap-1 ${isDark ? "text-slate-300 hover:text-white" : "text-slate-600 hover:text-slate-900"}`}
                    data-testid="clover-diag-copy-webhook-url-btn"
                  >
                    <Copy className="w-3.5 h-3.5" /> Copy
                  </button>
                </div>
                <p className={`text-xs font-mono break-all ${isDark ? "text-cyan-300" : "text-cyan-700"}`}
                   data-testid="clover-diag-webhook-url">
                  {data.webhook_callback_url}
                </p>
              </div>
            )}

            {/* Sample auth URL */}
            <div className={`p-3 rounded-lg ${isDark ? "bg-slate-900/50 border border-slate-700" : "bg-slate-50 border border-slate-200"}`}>
              <div className="flex items-center justify-between mb-1">
                <span className={`text-xs font-semibold uppercase tracking-wide ${sub}`}>Sample auth URL (merchant would land here)</span>
                <button
                  onClick={copyUrl}
                  className={`text-xs inline-flex items-center gap-1 ${isDark ? "text-slate-300 hover:text-white" : "text-slate-600 hover:text-slate-900"}`}
                  data-testid="clover-diag-copy-url-btn"
                >
                  {copied ? <><Check className="w-3.5 h-3.5" /> Copied</> : <><Copy className="w-3.5 h-3.5" /> Copy</>}
                </button>
              </div>
              <a
                href={data.sample_auth_url}
                target="_blank"
                rel="noreferrer"
                className={`text-xs font-mono break-all inline-flex items-center gap-1 ${isDark ? "text-cyan-300 hover:text-cyan-200" : "text-cyan-700 hover:text-cyan-900"}`}
                data-testid="clover-diag-auth-url-link"
              >
                {data.sample_auth_url}
                <ExternalLink className="w-3 h-3 flex-shrink-0" />
              </a>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function Fact({ label, value, isDark, ok, mono }) {
  const sub = isDark ? "text-slate-400" : "text-slate-500";
  const val = isDark ? "text-slate-100" : "text-slate-800";
  const okColor = ok === true
    ? (isDark ? "text-emerald-400" : "text-emerald-600")
    : ok === false
      ? (isDark ? "text-amber-400" : "text-amber-700")
      : val;
  return (
    <div className={`p-2.5 rounded-md ${isDark ? "bg-slate-900/40" : "bg-slate-50"}`}>
      <p className={`text-[10px] uppercase tracking-wide ${sub}`}>{label}</p>
      <p className={`text-sm ${mono ? "font-mono" : "font-medium"} ${okColor}`}>{value}</p>
    </div>
  );
}
